import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    fetchTeams,
    fetchTournamentInfo,
    checkJersey,
    checkEmail,
    registerPlayer,
    updatePlayerFiles,
    uploadFile,
    fetchPaymentConfig,
    createStripeIntent,
    createPayPalOrder,
    capturePayPalOrder,
    confirmPayment,
} from "../api";
import "./RegisterPage.css";

const POSITIONS = ["GK", "DF", "MF", "FW"];
const REGIONS = [
    "Los Angeles",
    "Chicago",
    "Maryland",
    "New Jersey",
    "Colorado",
];

// ── SVG Icons ─────────────────────────────────────────────
const IconCalendar = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="3" y="4" width="18" height="18" rx="3" />
        <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
);
const IconDollar = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v10M9.5 9.5C9.5 8.4 10.6 7.5 12 7.5s2.5.9 2.5 2c0 2.5-5 2.5-5 5 0 1.1 1.1 2 2.5 2s2.5-.9 2.5-2" />
    </svg>
);
const IconClock = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 3" />
    </svg>
);
const IconLock = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
);
const IconUpload = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);
const IconCheck = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const IconArrow = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
);
const IconBack = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
);
const IconCard = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
    </svg>
);
const IconInfo = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 16v-4M12 8h.01" />
    </svg>
);

// ── Consent Modal ─────────────────────────────────────────
function ConsentModal({ onAgree, onClose }) {
    const [checked, setChecked] = useState(false);
    const today = new Date();
    const dateStr = `${today.getFullYear()}년 ${today.getMonth() + 1}월 ${today.getDate()}일 / ${today.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`;

    return (
        <div className="consent-overlay" onClick={onClose}>
            <div className="consent-modal" onClick={(e) => e.stopPropagation()}>
                <h2 className="consent-title">
                    개인정보 수집·이용 동의서
                    <span className="consent-title-en">
                        Personal Information Collection & Use Agreement
                    </span>
                </h2>
                <div className="consent-body">
                    <p className="consent-intro">
                        재미대한축구협회는 '제24회 재미대한축구협회장 배 전미
                        축구대회'에 Open Age 그룹에 선수등록 후 대회를
                        운영합니다. 이에, 개인정보 수집·이용을 위하여
                        개인정보보호법에 따라 귀하의 동의를 받고자 합니다.
                    </p>
                    <p className="consent-intro consent-intro-en">
                        The Korean American Soccer Association (KASA) organizes
                        the 24th KASA President's Cup National Soccer Tournament
                        for the Open Age group. In accordance with personal
                        information protection laws, we request your consent for
                        the collection and use of personal information.
                    </p>
                    <table className="consent-table">
                        <thead>
                            <tr>
                                <th>
                                    수집·이용 항목
                                    <span className="th-en">
                                        Items Collected
                                    </span>
                                </th>
                                <th>
                                    수집·이용 목적
                                    <span className="th-en">Purpose</span>
                                </th>
                                <th>
                                    보유 및 이용기간
                                    <span className="th-en">
                                        Retention Period
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    성명, 소속, 생년월일, 연락처
                                    <span className="td-en">
                                        Name, Team, Date of Birth, Contact
                                    </span>
                                </td>
                                <td>
                                    제24회 재미대한축구협회장 배 전미 축구대회
                                    선수 등록 및 대회 운영
                                    <span className="td-en">
                                        Player registration and tournament
                                        operations for the 24th KASA President's
                                        Cup
                                    </span>
                                </td>
                                <td>
                                    2년 / 2 Years
                                    <br />
                                    2027년 미주체전까지
                                    <span className="td-en">
                                        Until 2027 US Korean Games
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="consent-notes">
                        <p>
                            ※ 위의 개인정보 수집·이용에 대한 동의를 거부할
                            권리가 있습니다. 그러나 동의를 거부할 경우 참가선수
                            등록 및 대회에 참가할 수 없습니다.
                            <span className="note-en">
                                ※ You have the right to refuse consent for the
                                collection and use of personal information
                                above. However, refusal will result in inability
                                to register as a player or participate in the
                                tournament.
                            </span>
                        </p>
                        <p>
                            ※ 개인정보 제공자가 동의한 내용 외의 다른 목적으로
                            활용하지 않으며, 제공된 개인정보의 이용을 거부하고자
                            할 때에는 개인정보 관리책임자를 통해 열람, 정정,
                            삭제를 요구할 수 있음
                            <span className="note-en">
                                ※ Information will not be used for purposes
                                beyond what has been consented to. You may
                                request access, correction, or deletion through
                                the privacy officer.
                            </span>
                        </p>
                    </div>
                    <p className="consent-date">{dateStr}</p>
                    <p className="consent-recipient">
                        재미대한축구협회장 귀하
                        <span className="td-en">
                            To the President of the Korean American Soccer
                            Association
                        </span>
                    </p>
                </div>
                <div className="consent-check-row">
                    <label className="consent-check-label">
                        <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                        />
                        <span>
                            위 개인정보 수집·이용에 동의합니다.
                            <span className="check-en">
                                I agree to the collection and use of my personal
                                information.
                            </span>
                        </span>
                    </label>
                </div>
                <div className="consent-actions">
                    <button className="btn-ghost" onClick={onClose}>
                        취소 / Cancel
                    </button>
                    <button
                        className="btn-primary"
                        disabled={!checked}
                        onClick={() => checked && onAgree()}
                    >
                        동의하고 계속하기 / Agree & Continue <IconArrow />
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── File Upload ───────────────────────────────────────────
function FileUpload({ label, onFileSelected }) {
    const [preview, setPreview] = useState(null);
    const [filename, setFilename] = useState("");
    const inputRef = useRef();

    const handleChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFilename(file.name);
        if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (ev) => setPreview(ev.target.result);
            reader.readAsDataURL(file);
        } else {
            setPreview(null);
        }
        onFileSelected(file);
    };

    return (
        <div className="file-upload-field">
            <label>{label}</label>
            <div
                className={`file-drop ${filename ? "done" : "idle"}`}
                onClick={() => inputRef.current.click()}
            >
                {!filename ? (
                    <>
                        <span className="file-icon">
                            <IconUpload />
                        </span>
                        <span>Click to select</span>
                        <span className="file-hint">
                            JPG · PNG · PDF — max 10MB
                        </span>
                    </>
                ) : (
                    <>
                        {preview ? (
                            <img
                                src={preview}
                                alt="preview"
                                className="file-preview-img"
                            />
                        ) : (
                            <span
                                className="file-icon"
                                style={{ opacity: 0.6 }}
                            >
                                <IconCheck />
                            </span>
                        )}
                        <span className="file-done">{filename}</span>
                        <span className="file-change">Click to change</span>
                    </>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleChange}
                    hidden
                />
            </div>
        </div>
    );
}

// ── Payment Step ──────────────────────────────────────────
function PaymentStep({ formData, playerId, fee, onSuccess }) {
    const [payConfig, setPayConfig] = useState(null);
    const [method, setMethod] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const stripeRef = useRef(null);
    const mountedRef = useRef(false);

    useEffect(() => {
        fetchPaymentConfig().then((r) => {
            setPayConfig(r.data);
            if (r.data.stripe_publishable_key) {
                if (window.Stripe) {
                    setPayConfig((c) => ({ ...c, _stripeReady: true }));
                } else {
                    const s = document.createElement("script");
                    s.src = "https://js.stripe.com/v3/";
                    s.onload = () =>
                        setPayConfig((c) => ({ ...c, _stripeReady: true }));
                    document.head.appendChild(s);
                }
            }
            if (r.data.paypal_client_id && !window.paypal) {
                const s = document.createElement("script");
                s.src = `https://www.paypal.com/sdk/js?client-id=${r.data.paypal_client_id}&currency=USD`;
                s.onload = () =>
                    setPayConfig((c) => ({ ...c, _paypalReady: true }));
                document.head.appendChild(s);
            }
        });
    }, []);

    useEffect(() => {
        if (
            method !== "stripe" ||
            mountedRef.current ||
            !payConfig?._stripeReady
        )
            return;
        if (!window.Stripe) return;
        mountedRef.current = true;
        const stripe = window.Stripe(payConfig.stripe_publishable_key);
        const elements = stripe.elements();
        const card = elements.create("card", {
            style: {
                base: {
                    color: "#0a1628",
                    fontFamily: "Noto Sans KR, sans-serif",
                    fontSize: "15px",
                },
            },
        });
        card.mount("#stripe-card-element");
        stripeRef.current = { stripe, card };
    }, [method, payConfig]);

    const handleStripeSubmit = async () => {
        if (!stripeRef.current) {
            setError("카드 정보를 입력해주세요.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const { stripe, card } = stripeRef.current;
            const intentRes = await createStripeIntent(
                formData.email,
                playerId,
            );
            const { client_secret } = intentRes.data;
            const result = await stripe.confirmCardPayment(client_secret, {
                payment_method: { card },
            });
            if (result.error) {
                setError(result.error.message);
                setLoading(false);
            } else if (result.paymentIntent.status === "succeeded") {
                // 등록 레코드는 이미 존재 → paid로 확정만 하면 된다.
                // confirm이 실패해도 결제는 끝났고 webhook이 보정하므로
                // 성공 화면으로 진행한다.
                try {
                    await confirmPayment(
                        playerId,
                        "stripe",
                        result.paymentIntent.id,
                    );
                } catch (e) {
                    console.error("confirmPayment failed:", e);
                }
                onSuccess(playerId);
            }
        } catch (e) {
            setError(
                "결제 처리 중 오류가 발생했습니다 · Payment processing error.",
            );
            setLoading(false);
        }
    };

    const payWithPayPal = async () => {
        setLoading(true);
        setError("");
        try {
            const res = await createPayPalOrder();
            const orderId = res.data.order_id;
            window.paypal
                .Buttons({
                    createOrder: () => orderId,
                    onApprove: async () => {
                        await capturePayPalOrder(orderId);
                        // 등록 레코드는 이미 존재 → paid로 확정만 진행
                        try {
                            await confirmPayment(playerId, "paypal", orderId);
                        } catch (e) {
                            console.error("confirmPayment failed:", e);
                        }
                        onSuccess(playerId);
                    },
                    onError: () => {
                        setError("PayPal payment failed.");
                        setLoading(false);
                    },
                })
                .render("#paypal-button-container");
            setLoading(false);
        } catch (e) {
            setError("Failed to initialize PayPal.");
            setLoading(false);
        }
    };

    if (!payConfig)
        return (
            <p className="text-muted">
                결제 수단 로딩 중 / Loading payment options...
            </p>
        );

    return (
        <div className="payment-step">
            <div className="payment-amount">
                <span>등록비 / Registration Fee</span>
                <strong>${fee}</strong>
            </div>
            {!method && (
                <div className="payment-methods">
                    {payConfig.stripe_publishable_key && (
                        <button
                            className="pay-btn pay-stripe"
                            onClick={() => setMethod("stripe")}
                        >
                            <IconCard /> Pay with Card (Stripe)
                        </button>
                    )}
                    {payConfig.paypal_client_id && (
                        <button
                            className="pay-btn pay-paypal"
                            onClick={payWithPayPal}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="#003087"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M20.067 8.478c.492.315.844.825.983 1.39.455 1.815-.97 3.47-2.94 3.47h-1.4l-.44 2.78H14.2l1.6-10.12h3.267c.933 0 1.733.44 2.067 1.14l-.067.34z" />
                                <path
                                    d="M7.933 4.12H11.2c2.8 0 4.467 1.605 4.133 4.12-.4 2.933-2.533 4.52-5.2 4.52H8.4l-.667 4.12H5.2L7.933 4.12z"
                                    fill="#009cde"
                                />
                            </svg>
                            Pay with PayPal
                        </button>
                    )}
                    {!payConfig.stripe_publishable_key &&
                        !payConfig.paypal_client_id && (
                            <p className="text-muted">
                                결제 수단이 설정되지 않았습니다 · No payment
                                methods configured.
                            </p>
                        )}
                </div>
            )}
            {method === "stripe" && (
                <div className="stripe-form">
                    <div
                        id="stripe-card-element"
                        className="stripe-card-element"
                    />
                    <button
                        className="btn-primary"
                        onClick={handleStripeSubmit}
                        disabled={loading}
                    >
                        {loading
                            ? "처리 중 / Processing..."
                            : `$${fee} 결제하기 / Pay $${fee}`}{" "}
                        {!loading && <IconArrow />}
                    </button>
                </div>
            )}
            {method === "paypal" && <div id="paypal-button-container" />}
            {error && (
                <p className="error-msg">
                    <IconInfo /> {error}
                </p>
            )}
        </div>
    );
}

// ── Main ──────────────────────────────────────────────────
export default function RegisterPage() {
    const navigate = useNavigate();
    const [tournament, setTournament] = useState(null);
    const [teams, setTeams] = useState([]);
    const [step, setStep] = useState(1);
    const [playerId, setPlayerId] = useState(null);
    const [fee, setFee] = useState(20);
    const [pendingFiles, setPendingFiles] = useState({
        photo: null,
        license: null,
    });
    const [showConsent, setShowConsent] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [regError, setRegError] = useState("");

    const [form, setForm] = useState({
        team_id: "",
        team_name: "",
        first_name: "",
        last_name: "",
        name_korean: "",
        nationality: "US",
        is_pr: "",
        date_of_birth: "",
        email: "",
        phone: "",
        jersey_number: "",
        position: "",
    });

    useEffect(() => {
        fetchTournamentInfo().then((r) => {
            setTournament(r.data);
            setFee(r.data.registration_fee);
        });
        fetchTeams().then((r) => setTeams(r.data));
    }, []);

    const [formErrors, setFormErrors] = useState({});

    const set = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
        setFormErrors((e) => ({ ...e, [field]: "" }));
    };

    const handleNext = async (e) => {
        e.preventDefault();
        const errors = {};

        if (!pendingFiles.photo)
            errors.photo =
                "선수 사진을 첨부해주세요 · Player photo is required";
        if (!pendingFiles.license)
            errors.license =
                "운전면허증을 첨부해주세요 · Driver license is required";

        if (form.email) {
            try {
                const res = await checkEmail(form.email);
                if (res.data.taken)
                    errors.email =
                        "이미 등록된 이메일입니다 · This email is already registered";
            } catch (err) {
                console.error("Email check failed:", err);
            }
        }

        if (form.jersey_number && form.team_id) {
            try {
                const res = await checkJersey(
                    parseInt(form.team_id),
                    parseInt(form.jersey_number),
                );
                if (res.data.taken)
                    errors.jersey_number = `#${form.jersey_number}은 이미 사용 중입니다 · Jersey number already taken`;
            } catch (err) {
                console.error("Jersey check failed:", err);
                errors.jersey_number =
                    "등번호 확인 중 오류가 발생했습니다 · Could not verify jersey number";
            }
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const team = teams.find((t) => t.id === parseInt(form.team_id));
        if (team) setForm((f) => ({ ...f, team_name: team.name }));
        setShowConsent(true);
    };

    const handleConsentAgree = async () => {
        setShowConsent(false);
        setRegistering(true);
        setRegError("");
        try {
            // 1) 결제 전에 미결제(unpaid) 등록 레코드를 먼저 생성 → player_id 확보.
            //    이메일이 미결제 상태로 이미 있으면 백엔드가 같은 레코드를 재사용한다.
            const res = await registerPlayer(formData);
            const pid = res.data.player_id;

            // 2) 확보한 player_id로 서류 업로드
            const meta = {
                team_id: formData.team_id,
                team_name: formData.team_name || String(formData.team_id),
                player_id: pid,
                player_name: `${formData.first_name}_${formData.last_name}`,
            };
            const urls = {};
            if (pendingFiles.photo) {
                const r = await uploadFile(pendingFiles.photo, "photo", meta);
                urls.photo_url = r.data.key;
            }
            if (pendingFiles.license) {
                const r = await uploadFile(
                    pendingFiles.license,
                    "license",
                    meta,
                );
                urls.driver_license_url = r.data.key;
            }

            // 3) 파일 URL 저장
            if (urls.photo_url || urls.driver_license_url) {
                await updatePlayerFiles(pid, urls);
            }

            // 4) 모든 데이터가 안전하게 저장된 뒤에야 결제 단계로 이동
            setPlayerId(pid);
            setStep(2);
        } catch (e) {
            setRegError(
                e.response?.data?.error ||
                    "등록 처리 중 오류가 발생했습니다. 다시 시도해주세요 · Registration failed. Please try again.",
            );
        } finally {
            setRegistering(false);
        }
    };
    const handlePaymentSuccess = (pid) => {
        setPlayerId(pid);
        setStep(3);
    };

    const formData = {
        ...form,
        team_id: parseInt(form.team_id),
        jersey_number: form.jersey_number ? parseInt(form.jersey_number) : null,
        is_pr: form.is_pr !== "" ? parseInt(form.is_pr) : null,
    };

    const selectedTeam = teams.find((t) => t.id === parseInt(form.team_id));
    const isClosed = tournament && !tournament.registration_open;

    const teamsByRegion = REGIONS.reduce((acc, r) => {
        const filtered = teams.filter((t) => t.region === r);
        if (filtered.length > 0) acc[r] = filtered;
        return acc;
    }, {});
    const otherTeams = teams.filter((t) => !REGIONS.includes(t.region));

    return (
        <div className="register-page">
            <header className="reg-header">
                <div className="reg-header-top">
                    <div className="reg-header-back-row">
                        <button
                            className="back-btn"
                            onClick={() => navigate("/")}
                        >
                            <IconBack /> 뒤로 / Back
                        </button>
                    </div>
                    <div className="reg-header-center">
                        <h1 className="reg-title">
                            {tournament?.name || "Player Registration"}
                        </h1>
                        <div className="reg-meta">
                            {tournament?.tournament_date && (
                                <span className="reg-meta-item">
                                    <IconCalendar />{" "}
                                    {tournament.tournament_date}
                                </span>
                            )}
                            {tournament?.registration_fee != null && (
                                <span className="reg-meta-item">
                                    <IconDollar /> $
                                    {tournament.registration_fee}
                                </span>
                            )}
                            {tournament?.registration_deadline && (
                                <span
                                    className={`reg-meta-item ${isClosed ? "deadline-closed" : "deadline-open"}`}
                                >
                                    {isClosed ? <IconLock /> : <IconClock />}
                                    {isClosed
                                        ? "마감"
                                        : `~${tournament.registration_deadline}`}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="reg-steps">
                    {[
                        ["정보 입력", "Info"],
                        ["결제", "Payment"],
                        ["완료", "Done"],
                    ].map(([s, en], i) => (
                        <>
                            <div
                                key={s}
                                className={`reg-step ${step === i + 1 ? "active" : step > i + 1 ? "done" : ""}`}
                            >
                                <span className="step-num">
                                    {step > i + 1 ? "✓" : i + 1}
                                </span>
                                <span className="step-label">
                                    {s}{" "}
                                    <span className="step-label-en">{en}</span>
                                </span>
                            </div>
                            {i < 2 && (
                                <div key={`line-${i}`} className="step-line" />
                            )}
                        </>
                    ))}
                </div>
            </header>

            <main className="reg-main">
                {isClosed && step === 1 && (
                    <div className="closed-banner">
                        <IconLock /> 등록이 마감되었습니다 · Registration is
                        closed. 주최 측에 문의해주세요 · Please contact the
                        organizer.
                    </div>
                )}

                {regError && step === 1 && (
                    <div className="closed-banner">
                        <IconInfo /> {regError}
                    </div>
                )}

                {step === 1 && !isClosed && (
                    <form className="reg-form" onSubmit={handleNext}>
                        <div className="form-section">
                            <h2 className="section-label">01 — 팀 선택</h2>
                            <select
                                value={form.team_id}
                                onChange={set("team_id")}
                                required
                            >
                                <option value="" disabled>
                                    SELECT YOUR TEAM
                                </option>
                                {teams.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.name} ({t.player_count}/
                                        {t.max_players}명)
                                    </option>
                                ))}
                            </select>
                            {selectedTeam && (
                                <p className="team-note">
                                    {selectedTeam.player_count} /{" "}
                                    {selectedTeam.max_players}명 등록됨 ·
                                    Registered
                                </p>
                            )}
                        </div>

                        <div className="form-section">
                            <h2 className="section-label">02 — 개인 정보</h2>
                            <div className="form-grid">
                                <div className="field">
                                    <label>First Name *</label>
                                    <input
                                        type="text"
                                        value={form.first_name}
                                        onChange={set("first_name")}
                                        required
                                    />
                                </div>
                                <div className="field">
                                    <label>Last Name *</label>
                                    <input
                                        type="text"
                                        value={form.last_name}
                                        onChange={set("last_name")}
                                        required
                                    />
                                </div>
                                <div className="field">
                                    <label>한글 이름 *</label>
                                    <input
                                        type="text"
                                        value={form.name_korean}
                                        onChange={set("name_korean")}
                                        required
                                    />
                                </div>
                                <div className="field">
                                    <label>Date of Birth *</label>
                                    <input
                                        type="date"
                                        value={form.date_of_birth}
                                        onChange={set("date_of_birth")}
                                        required
                                    />
                                </div>
                                <div className="field">
                                    <label>Email *</label>
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        required
                                    />
                                    {formErrors.email && (
                                        <span className="field-error">
                                            {formErrors.email}
                                        </span>
                                    )}
                                </div>
                                <div className="field">
                                    <label>Phone Number *</label>
                                    <input
                                        type="tel"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        required
                                    />
                                </div>
                                <div className="field">
                                    <label>Nationality *</label>
                                    <select
                                        value={form.nationality}
                                        onChange={set("nationality")}
                                        required
                                    >
                                        <option value="US">
                                            United States
                                        </option>
                                        <option value="KR">South Korea</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                                {form.nationality !== "US" && (
                                    <div className="field">
                                        <label>
                                            Permanent Resident (영주권) *
                                        </label>
                                        <select
                                            value={form.is_pr}
                                            onChange={set("is_pr")}
                                            required
                                        >
                                            <option value="">선택하세요</option>
                                            <option value="1">
                                                Yes — 영주권 소지
                                            </option>
                                            <option value="0">
                                                No — 미소지
                                            </option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-section">
                            <h2 className="section-label">03 — 선수 정보</h2>
                            <div className="form-grid">
                                <div className="field">
                                    <label>Jersey Number (등번호) *</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="99"
                                        value={form.jersey_number}
                                        onChange={set("jersey_number")}
                                    />
                                    {formErrors.jersey_number && (
                                        <span className="field-error">
                                            {formErrors.jersey_number}
                                        </span>
                                    )}
                                </div>
                                <div className="field">
                                    <label>Position (포지션) *</label>
                                    <select
                                        value={form.position}
                                        onChange={set("position")}
                                    >
                                        <option value="" disabled>
                                            SELECT YOUR POSITION
                                        </option>
                                        {POSITIONS.map((p) => (
                                            <option key={p} value={p}>
                                                {p}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h2 className="section-label">04 — 서류 첨부</h2>
                            <p className="file-note">
                                <IconInfo /> 서류는 결제 완료 후 자동
                                업로드됩니다 · Documents will be uploaded
                                automatically after payment.
                            </p>
                            <div className="form-grid">
                                <div>
                                    <FileUpload
                                        label="선수 사진 (Player Photo)"
                                        onFileSelected={(f) => {
                                            setPendingFiles((p) => ({
                                                ...p,
                                                photo: f,
                                            }));
                                            setFormErrors((e) => ({
                                                ...e,
                                                photo: "",
                                            }));
                                        }}
                                    />
                                    {formErrors.photo && (
                                        <span className="field-error">
                                            {formErrors.photo}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <FileUpload
                                        label="운전면허증 (Driver License)"
                                        onFileSelected={(f) => {
                                            setPendingFiles((p) => ({
                                                ...p,
                                                license: f,
                                            }));
                                            setFormErrors((e) => ({
                                                ...e,
                                                license: "",
                                            }));
                                        }}
                                    />
                                    {formErrors.license && (
                                        <span className="field-error">
                                            {formErrors.license}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form-footer">
                            <p className="fee-note">
                                등록비 / Registration Fee &nbsp;&nbsp;
                                <strong>${fee}</strong>
                            </p>
                            <button type="submit" className="btn-primary">
                                다음: 결제 / Next: Payment <IconArrow />
                            </button>
                        </div>
                    </form>
                )}

                {step === 2 && (
                    <div className="payment-card">
                        <div className="payment-card-header">
                            <h2>결제 / Payment</h2>
                            <p className="payment-sub">
                                결제 완료 후 등록이 확정됩니다. · Registration
                                is confirmed upon payment.
                            </p>
                        </div>
                        <div className="payment-card-body">
                            <PaymentStep
                                formData={formData}
                                playerId={playerId}
                                fee={fee}
                                onSuccess={handlePaymentSuccess}
                            />
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="success-card">
                        <div className="success-icon">
                            <IconCheck />
                        </div>
                        <h2>등록 완료 / Registration Complete!</h2>
                        <p>
                            결제 및 등록이 완료되었습니다 · Payment and
                            registration are complete.
                            <br />
                            관리자 승인 후 최종 확정됩니다 · Final confirmation
                            pending admin approval.
                        </p>
                        <div className="success-details">
                            <span>접수 번호 / Ref No.</span>
                            <strong>
                                #{String(playerId).padStart(5, "0")}
                            </strong>
                        </div>
                        <p className="success-note">
                            접수 번호를 저장해 두세요 · Please save your
                            reference number.
                        </p>
                        <button
                            className="btn-ghost"
                            onClick={() => {
                                setStep(1);
                                setPlayerId(null);
                                setForm({
                                    team_id: "",
                                    team_name: "",
                                    first_name: "",
                                    last_name: "",
                                    name_korean: "",
                                    nationality: "US",
                                    is_pr: "",
                                    date_of_birth: "",
                                    email: "",
                                    phone: "",
                                    jersey_number: "",
                                    position: "",
                                });
                                setPendingFiles({ photo: null, license: null });
                            }}
                        >
                            다른 선수 등록하기 / Register Another Player
                        </button>
                    </div>
                )}
            </main>

            {showConsent && (
                <ConsentModal
                    onAgree={handleConsentAgree}
                    onClose={() => setShowConsent(false)}
                />
            )}

            {registering && (
                <div className="consent-overlay">
                    <div
                        className="consent-modal"
                        style={{ textAlign: "center", padding: "40px" }}
                    >
                        <p style={{ fontSize: "16px", fontWeight: 600 }}>
                            등록 처리 중 / Processing registration...
                        </p>
                        <p className="text-muted" style={{ marginTop: "8px" }}>
                            잠시만 기다려주세요 · Please wait.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
